import { Box } from '@chakra-ui/react';
import {
  GetAssessmentCurrencyConversionQuery_,
  useGetEsrsAssessmentShortDetailsQuery,
  useGetSpecificMaterialityQuery,
} from 'models';
import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'utils/translation';
import { EsrsReportStandardView, ReportAnswers } from './Report.types';
import { RenderOnViewportEntry } from './ReportUtils';
import { StructureNode } from '../Assessment/Structure';
import {
  appedixMetricRefs,
  appedixStandardRefs,
} from './DocGenerator/CustomTables/other-eu-legislations';
import ReportContentContainer from './ReportUIComponents/ReportContentContainer';

const DrCard = React.lazy(() => import('./ReportComponents/DrCard'));

export const ReportStandardPage = ({
  standardIndex,
  setStandardIndex,
  standards,
  reportRef,
  reportAnswers,
  isPreview,
  currencyConversionData,
  groupMetricsRefs,
  structure,
  year,
}: {
  standardIndex: number;
  setStandardIndex: (val: number) => void;
  standards?: EsrsReportStandardView[];
  reportAnswers: ReportAnswers;
  reportRef: RefObject<HTMLDivElement | null>;
  isPreview: boolean;
  currencyConversionData?: GetAssessmentCurrencyConversionQuery_['esrs_AssessmentCurrencyConversion'];
  groupMetricsRefs: string[];
  structure: StructureNode[][][];
  year?: number;
}) => {
  const { esrsAssessmentId = '' } = useParams();
  const { t } = useTranslation('esrs');
  const [searchParams] = useSearchParams();
  const urlDrRef = useMemo(() => searchParams.get('drRef'), [searchParams]);
  const urlMetricRef = useMemo(() => searchParams.get('metricRef'), [searchParams]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const handleScroll = () => {
    setShowBackToTop((reportRef.current?.scrollTop ?? 0) > 0);
  };

  const { data: assessmentData, loading: assessmentLoading } =
    useGetEsrsAssessmentShortDetailsQuery({
      variables: { esrsAssessmentId },
      skip: !esrsAssessmentId || standardIndex !== 0,
    });

  const { data: materialityData, loading: materialityLoading } = useGetSpecificMaterialityQuery({
    variables: {
      esrsAssessmentId,
      standardRefs: appedixStandardRefs,
      metricRefs: appedixMetricRefs,
    },
    skip: !esrsAssessmentId,
  });

  const selectedStandard = useMemo(() => standards?.[standardIndex], [standards, standardIndex]);

  const reportTitle = useMemo(
    () =>
      `${
        t('assessment.report.reportTitle') +
        ' for ' +
        assessmentData?.assessment?.company.name +
        ' ' +
        assessmentData?.assessment?.reportingYear
      }`,
    [assessmentData]
  );

  useEffect(() => {
    reportRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [standardIndex]);

  useEffect(() => {
    if (urlDrRef) {
      const element = document.getElementById(urlDrRef);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [urlDrRef, standardIndex, standards]);

  return (
    <ReportContentContainer
      handleScroll={handleScroll}
      reportTitle={reportTitle}
      reportRef={reportRef}
      showBackToTop={showBackToTop}
      standardIndex={standardIndex}
      setStandardIndex={setStandardIndex}
      standards={standards}
      loading={assessmentLoading || materialityLoading}
      selectedStandard={selectedStandard}
    >
      {selectedStandard?.disclosureRequirementRefs.map((dr, index) => (
        <Box id={dr.key} w="100%" key={dr.key}>
          <RenderOnViewportEntry threshold={0} key={dr.key}>
            <DrCard
              isPreview={isPreview}
              dr={dr}
              reportAnswers={reportAnswers}
              urlMetricRef={urlMetricRef ?? ''}
              standard={selectedStandard}
              parentAssessmentId={assessmentData?.assessment?.parentAssessmentId}
              currencyConversionData={currencyConversionData}
              groupMetricsRefs={groupMetricsRefs}
              structure={structure}
              year={year}
              isEndOfReport={
                standardIndex === (standards?.length ?? 0) - 1 &&
                index === (selectedStandard?.disclosureRequirementRefs.length ?? 0) - 1
              }
              materialityData={materialityData}
            />
          </RenderOnViewportEntry>
        </Box>
      ))}
    </ReportContentContainer>
  );
};
